import { useEffect, useRef } from 'react';
import ApiHelper from 'helpers/Api';
import { AppProvider } from 'common/context';
import { AppRouter } from 'AppRouter';
import { useSuperState } from "helpers/hooks";
import SessionConnector from "pages/archive/components/SessionConnector";
import common from "gmetrix-labs-common/es6";
import { Spinner } from "react-bootstrap";
import { FormattedMessage, IntlProvider } from 'react-intl';

const { Err } = common.helpers.log;

// Internationalization setup
const localeMessagesLibrary = common.lang;
const defaultLocale = 'en-US';

const api = new ApiHelper();

export const App = props => {

    const [state, setState] = useSuperState({
        error: false,
        loading: true,
        locale: defaultLocale,
        localeMessages: localeMessagesLibrary[defaultLocale],
        siteUrl: null,
        env: null,
        isLoggedIn: false,
        fleets: []
    });

    const { locale, localeMessages, isLoggedIn, loading, fleets } = state;

    const session_connector_ref = useRef(null);

    const fetchSiteBasics = async () => {

        try {

            const { config } = await api.get('/api/site/basics');
            const { site_url, env, fleets } = config;

            setState({
                siteUrl: site_url,
                env,
                fleets
            });

        } catch (e){

            const error = e?.err || e?.message || e;
            Err(error);

            setState({
                error
            });

        }

    };

    const setLocale = async locale => {

        try {

            if (!locale || !locale?.length) return;

            setState(prev => {
                prev.locale = locale;
                prev.localeMessages = localeMessagesLibrary?.[locale] || localeMessagesLibrary['en-US'];
                return prev;
            });

            this.setCookie('locale', locale);

        } catch (e){

            const error = e?.err || e?.message || e;
            Err(error);

        }

    };

    // Checks if the server considers us authenticated
    const verifyLogin = async () => {

        try {

            return await api.post('/api/auth/check');

        } catch (e){

            const error = e?.err || e?.message || e;
            Err(error);

        }

    };

    // Attempt authentication via Microsoft SSO
    const tryMSLogin = async () => {

        // Open the login in a new window
        const url = '/api/auth/new/microsoft';
        window.open(url, 'Sign in with Microsoft', 'width=600,height=600,scrollbars=yes');

        window.addEventListener('storage', async function(event) {

            if (event?.key !== 'msAuth') return;

            // TODO Figure out why this is failing
            // If logged in, do a hard refresh to the redirect page
            if (await verifyLogin()) {
                location.reload();
            }

        });

    };

    // Asks the server to consider us unauthenticated
    const tryServerLogout = async () => {

        try {

            setState({ loading: true });
            await api.post('/api/auth/logout');

        } catch (e) {

            const error = e?.err || e?.message || e;
            Err(error);

            setState({
                loading: false,
                error
            });

        }

    };

    const logout = async () => {

        await tryServerLogout();
        location.reload();

    };

    const context = {
        api,
        session_connector: session_connector_ref?.current,
        setLocale,
        tryMSLogin,
        logout,
        isLoggedIn,
        fleets
    };

    useEffect(() => {

        Promise.all([fetchSiteBasics(), verifyLogin()]).then(([_, isLoggedIn]) => {
            setState({ isLoggedIn, loading: false });
        });

    }, []);

    return (
        <IntlProvider locale={locale} messages={localeMessages}>
            <AppProvider value={{ ...context }}>

                {loading ?
                    (
                        <div className="d-flex flex-column align-items-center justify-content-center vh-100 gap-3">
                            <Spinner size="lg" variant="primary" />
                            <span className="text-center lead">
                                <FormattedMessage
                                    id="label.loading"
                                    defaultMessage="Loading..."
                                />
                            </span>
                        </div>
                    )
                    :
                    (
                        <AppRouter />
                    )
                }

                <SessionConnector ref={session_connector_ref}/>

            </AppProvider>
        </IntlProvider>
    );

};
